import { createContext, type Key, type ReactNode, use, useMemo, useState } from "react";

type RefreshContextValue = {
  readonly refreshKey: Key;
  readonly updateRefreshKey: () => void;
};

const RefreshContext = createContext<RefreshContextValue>({ refreshKey: "", updateRefreshKey: () => {} });

type RefreshProviderProps = {
  readonly children: ReactNode;
};

export const RefreshProvider = ({ children }: RefreshProviderProps) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const value = useMemo(() => ({ refreshKey, updateRefreshKey: () => setRefreshKey((k) => k + 1) }), [refreshKey]);
  return <RefreshContext value={value}>{children}</RefreshContext>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useUpdateRefreshKey = () => {
  const context = use(RefreshContext);
  return context.updateRefreshKey;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useRefreshState = <T,>(defaultValue: T) => {
  const [state, setState] = useState<T>(defaultValue);
  const context = use(RefreshContext);

  const [prevKey, setPrevKey] = useState(context.refreshKey);
  if (context.refreshKey !== prevKey) {
    setPrevKey(context.refreshKey);
    setState(defaultValue);
  }

  return [state, setState] as const;
};
